import React, { useRef, useState } from 'react';
import { Button, FestiveButton, IconRenderer, ModuleBase, ResponsiveImage } from '@/components';
import Flicking, { FlickingError } from '@egjs/react-flicking';
import CarouselPagination from '@/components/general/CarouselPagination';
import '@egjs/react-flicking/dist/flicking.css';
import Arrow from '@/assets/arrowForward.svg';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectCampaignIcon, selectIsCampaignActive } from '@/store/index.js';

const MediaCarouselFullBleedModulWithText = ({ data }) => {
  const carousel = useRef();
  const [campaignActive] = [useSelector(selectIsCampaignActive)];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [panelCount, setPanelCount] = useState(0);

  const willChange = (e) => {
    setCurrentIndex(e.index);
  };

  const onReady = () => {
    setPanelCount(carousel.current.panelCount);
  };

  const move = async (dir) => {
    try {
      if (dir > 0) {
        await carousel.current.next();
      } else {
        await carousel.current.prev();
      }
    } catch (err) {
      if (!(err instanceof FlickingError)) {
        throw new Error(err);
      }
    }
  };

  return (
    <ModuleBase
      data={data}
      className={classNames(
        'h-[min(626px,100svh)] md:h-[min(700px,100svh)] relative',
        campaignActive && data.campaignMode ? 'campaign' : '',
      )}
    >
      <Flicking
        ref={carousel}
        align="prev"
        useResizeObserver={true}
        horizontal={true}
        circular={true}
        circularFallback="bound"
        onWillChange={willChange}
        onReady={onReady}
      >
        {data.mediaSlides.map((slide, index) => (
          <div
            key={index}
            className="h-[min(626px,100svh)] md:h-[min(700px,100svh)] w-[85%] md:w-[85.80%] lg:w-[81.9%] relative after:content-[''] after:z-10 after:absolute after:inset-0 after:bg-black/50 after:pointer-events-none"
          >
            <ResponsiveImage
              image={slide.image}
              widths={{ xs: 430, sm: 768, md: 768, lg: 1920, xl: 1920, xxl: 1920 }}
              heights={{ xs: 480, sm: 480, md: 480, lg: 700, xl: 700, xxl: 700 }}
              className={classNames(
                "absolute z-0 after:content-[''] after:absolute after:z-0 after:inset-0 after:bg-black/50 after:duration-300 after:transition-all",
                currentIndex !== index ? 'after:opacity-100' : 'after:opacity-0',
              )}
              id="carouselItem"
              showImageCredit
            />
            <div
              className={classNames(
                'absolute w-[calc(100%-32px)] md:w-[475px] z-20 top-1/2 left-4 text-white md:left-[3.125rem] lg:left-[8.75rem] xxl:left-[clamp(140px,calc(50vw-580px),calc(50vw-655px))] flex flex-col transition-all duration-1000 ease-out delay-200',
                currentIndex !== index ? '-translate-y-1/4 opacity-0' : '-translate-y-1/2 opacity-100',
              )}
            >
              {campaignActive && data?.campaignMode && (
                <IconRenderer selector={selectCampaignIcon} className="fill-current mb-6" />
              )}
              {slide.preHeading && (
                <p className="font-cera text-sm leading-4 -tracking-[0.02em] max-w-[426px] mb-6">{slide.preHeading}</p>
              )}
              {slide.headingTitle && (
                <p className="font-ivy-ora text-headingReg -tracking-[0.01em] md:text-title-l md:max-w-[475px] md:leading-60 md:-tracking-[0.03em] mb-4 md:mb-8">
                  {slide.headingTitle}
                </p>
              )}
              {slide.description && (
                <div
                  className="font-cera text-paragraph leading-[1.375rem] md:text-large-paragraph mb-4 md:max-w-[322px] md:mb-8"
                  dangerouslySetInnerHTML={{ __html: slide.description }}
                />
              )}
              {slide.primaryCTA ? (
                campaignActive && data?.campaignMode ? (
                  <FestiveButton link={slide.primaryCTA} />
                ) : (
                  <Button link={slide.primaryCTA} theme="light" type="primary" />
                )
              ) : null}
            </div>
          </div>
        ))}
      </Flicking>

      <CarouselPagination
        current={currentIndex}
        total={panelCount}
        theme={'light'}
        className={`pagination mx-auto z-10 absolute right-4 lg:right-6 bottom-2
                    [&_.currentPage]:text-secondary-coolsand [&_.totalPage]:text-secondary-coolsand
                    [&_.currentPage]:lg:text-[80px] [&_.currentPage]:lg:leading-[72px] [&_.currentPage]:lg:-tracking-ultraWide
                    [&_.totalPage]:lg:text-[80px] [&_.totalPage]:lg:leading-[72px] [&_.totalPage]:lg:-tracking-ultraWide
                    lg:bottom-6
        `}
      />

      <div
        className={`hidden buttonAnim
                    lg:absolute  lg:top-1/2 lg:-translate-y-1/2 lg:z-20 lg:size-[88px] 
                    lg:bg-secondary-warmsand/30 lg:rounded-full
                    lg:flex lg:justify-between lg:items-center
                    lg:left-[81.9%]
                    lg:-translate-x-[calc(50%)]
                    
      `}
        // style={{ left: `${arrowPosition}px` }}
      >
        <button
          className="p-0 size-11 sm:size-12 z-10  flex justify-center items-center"
          onClick={move}
          aria-label="Previous"
        >
          <Arrow className="" />
        </button>
        <div className="divider rounded-full bg-white size-0.5"></div>
        <button
          className="p-0 size-11 sm:size-12 z-10 rotate-180  flex justify-center items-center"
          onClick={() => move(1)}
          aria-label="Next"
        >
          <Arrow />
        </button>
      </div>
    </ModuleBase>
  );
};

export default MediaCarouselFullBleedModulWithText;
